import { useState, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight, RefreshCw, CalendarDays, FlaskConical } from 'lucide-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiError, api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FreeSlot {
  start: string
  end: string
}

interface VendorAppointment {
  id: string
  title: string | null
  startTime: string
  endTime: string
  contactId: string
  status: string | null
}

interface VendorEntry {
  sellerId: string
  name: string
  calendarId: string
  ghlUserId: string
  appointments: VendorAppointment[]
  freeSlots: FreeSlot[]
  fetchError?: string
  freeSlotsError?: string
}

interface VendorCalendarsResponse {
  vendors: VendorEntry[]
  cachedAt: string
  start: string
  end: string
  cacheExpiresAt: string
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GRID_START_HOUR = 7
const GRID_END_HOUR = 21
const TOTAL_HOURS = GRID_END_HOUR - GRID_START_HOUR
const PX_PER_HOUR = 64

// Paleta de cores por vendedor — background e borda
const VENDOR_PALETTE = [
  { bg: 'rgba(99,102,241,0.85)',  border: '#6366f1' },  // indigo
  { bg: 'rgba(245,158,11,0.85)',  border: '#f59e0b' },  // amber
  { bg: 'rgba(16,185,129,0.85)',  border: '#10b981' },  // emerald
  { bg: 'rgba(239,68,68,0.85)',   border: '#ef4444' },  // red
  { bg: 'rgba(59,130,246,0.85)',  border: '#3b82f6' },  // blue
  { bg: 'rgba(139,92,246,0.85)',  border: '#8b5cf6' },  // violet
  { bg: 'rgba(236,72,153,0.85)',  border: '#ec4899' },  // pink
  { bg: 'rgba(20,184,166,0.85)',  border: '#14b8a6' },  // teal
]

function vendorColor(idx: number) {
  return VENDOR_PALETTE[idx % VENDOR_PALETTE.length]!
}

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const MONTH_LABELS = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimeHHMM(d: Date): string {
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function addDays(d: Date, n: number): Date {
  const result = new Date(d)
  result.setDate(result.getDate() + n)
  return result
}

function localYMD(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function weekRange(anchor: Date): { start: Date; end: Date } {
  const day = anchor.getDay()
  const start = addDays(anchor, -day)
  const end = addDays(start, 6)
  return { start, end }
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

async function fetchVendorCalendars(
  start: string,
  end: string,
  refresh = false,
): Promise<VendorCalendarsResponse> {
  const params = new URLSearchParams({ start, end })
  if (refresh) params.set('refresh', 'true')
  return api<VendorCalendarsResponse>(`/admin/ghl/vendor-calendars?${params.toString()}`)
}

// ---------------------------------------------------------------------------
// Appointment blocks with overlap layout
// ---------------------------------------------------------------------------

interface PlacedAppointment {
  appt: VendorAppointment
  vendorIdx: number
  vendorName: string
  col: number
  totalCols: number
}

/**
 * Para um conjunto de appointments num mesmo dia, detecta sobreposições e
 * distribui em colunas paralelas para que fiquem visíveis lado a lado.
 */
function layoutAppointments(
  vendors: VendorEntry[],
  day: Date,
): PlacedAppointment[] {
  const dayStartMs = new Date(day).setHours(0, 0, 0, 0)
  const dayEndMs = new Date(day).setHours(23, 59, 59, 999)

  // Coleta todos os appointments do dia com índice do vendedor
  const all: Array<{ appt: VendorAppointment; vendorIdx: number; vendorName: string; startMs: number; endMs: number }> = []
  vendors.forEach((v, idx) => {
    v.appointments.forEach((appt) => {
      const s = new Date(appt.startTime).getTime()
      const e = new Date(appt.endTime).getTime()
      if (s > dayEndMs || e < dayStartMs) return
      all.push({ appt, vendorIdx: idx, vendorName: v.name, startMs: s, endMs: e })
    })
  })

  all.sort((a, b) => a.startMs - b.startMs)

  // Greedy column assignment: atribui coluna mínima que não conflita
  const colEndTimes: number[] = []
  const assigned: number[] = []

  all.forEach((item) => {
    let col = colEndTimes.findIndex((end) => end <= item.startMs)
    if (col === -1) {
      col = colEndTimes.length
      colEndTimes.push(item.endMs)
    } else {
      colEndTimes[col] = item.endMs
    }
    assigned.push(col)
  })

  const totalCols = Math.max(colEndTimes.length, 1)

  return all.map((item, i) => ({
    appt: item.appt,
    vendorIdx: item.vendorIdx,
    vendorName: item.vendorName,
    col: assigned[i]!,
    totalCols,
  }))
}

// ---------------------------------------------------------------------------
// Free slot layout helpers
// ---------------------------------------------------------------------------

interface PlacedFreeSlot {
  slot: FreeSlot
  vendorIdx: number
  vendorName: string
}

function layoutFreeSlots(vendors: VendorEntry[], day: Date): PlacedFreeSlot[] {
  const dayStartMs = new Date(day).setHours(0, 0, 0, 0)
  const dayEndMs   = new Date(day).setHours(23, 59, 59, 999)
  const result: PlacedFreeSlot[] = []
  vendors.forEach((v, idx) => {
    for (const slot of v.freeSlots) {
      const s = new Date(slot.start).getTime()
      const e = new Date(slot.end).getTime()
      if (s > dayEndMs || e < dayStartMs) continue
      result.push({ slot, vendorIdx: idx, vendorName: v.name })
    }
  })
  return result
}

// ---------------------------------------------------------------------------
// DayColumn
// ---------------------------------------------------------------------------

interface DayColumnProps {
  day: Date
  vendors: VendorEntry[]
  isToday: boolean
}

function DayColumn({ day, vendors, isToday }: DayColumnProps): React.ReactElement {
  const gridStartMs = new Date(day).setHours(GRID_START_HOUR, 0, 0, 0)
  const gridEndMs   = new Date(day).setHours(GRID_END_HOUR, 0, 0, 0)
  const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => GRID_START_HOUR + i)
  const placed = layoutAppointments(vendors, day)
  const freeSlots = layoutFreeSlots(vendors, day)

  return (
    <div
      className={cn('relative border-r', isToday && 'bg-primary/5')}
      style={{ height: TOTAL_HOURS * PX_PER_HOUR }}
    >
      {/* Hour grid lines */}
      {hours.map((h) => (
        <div
          key={h}
          className="absolute inset-x-0 border-b border-border/40"
          style={{ top: (h - GRID_START_HOUR) * PX_PER_HOUR }}
        />
      ))}

      {/* Free slot blocks — behind appointments */}
      {freeSlots.map(({ slot, vendorIdx, vendorName }) => {
        const start = new Date(slot.start)
        const end   = new Date(slot.end)

        const clampedStart = Math.max(start.getTime(), gridStartMs)
        const clampedEnd   = Math.min(end.getTime(), gridEndMs)
        if (clampedEnd <= clampedStart) return null

        const topPx    = ((clampedStart - gridStartMs) / (1000 * 60 * 60)) * PX_PER_HOUR
        const heightPx = Math.max(((clampedEnd - clampedStart) / (1000 * 60 * 60)) * PX_PER_HOUR, 8)
        const color = vendorColor(vendorIdx)

        return (
          <div
            key={`fs-${vendorIdx}-${slot.start}`}
            className="absolute inset-x-0.5 rounded-sm"
            style={{
              top: topPx,
              height: heightPx,
              backgroundColor: color.border + '22',
              borderLeft: `2px solid ${color.border}66`,
            }}
            title={`Disponível · ${vendorName} · ${formatTimeHHMM(start)}–${formatTimeHHMM(end)}`}
          />
        )
      })}

      {/* Appointment blocks */}
      {placed.map(({ appt, vendorIdx, vendorName, col, totalCols }) => {
        const start = new Date(appt.startTime)
        const end   = new Date(appt.endTime)

        const clampedStart = Math.max(start.getTime(), gridStartMs)
        const clampedEnd   = Math.min(end.getTime(), gridEndMs)
        if (clampedEnd <= clampedStart) return null

        const topPx    = ((clampedStart - gridStartMs) / (1000 * 60 * 60)) * PX_PER_HOUR
        const heightPx = Math.max(((clampedEnd - clampedStart) / (1000 * 60 * 60)) * PX_PER_HOUR, 18)

        const colW  = 100 / totalCols
        const left  = `${col * colW + 0.5}%`
        const width = `${colW - 1}%`
        const color = vendorColor(vendorIdx)
        // Emoji de status (com base no `status`, que espelha o appointmentStatus
        // do GHL): ✅ confirmado, 🔄 reagendado, ⏳ pendente / ainda não confirmado.
        const statusEmoji =
          appt.status === 'confirmed'
            ? '✅'
            : appt.status === 'rescheduled'
              ? '🔄'
              : '⏳'

        return (
          <div
            key={appt.id}
            className="absolute rounded overflow-hidden cursor-default"
            style={{
              top: topPx,
              height: heightPx,
              left,
              width,
              backgroundColor: color.bg,
              borderLeft: `3px solid ${color.border}`,
            }}
            title={`${statusEmoji} ${vendorName} · ${appt.title ?? 'Sem título'} · ${formatTimeHHMM(start)}–${formatTimeHHMM(end)}${appt.status ? ` · ${appt.status}` : ''}`}
          >
            <span className="block px-1 pt-0.5 text-[10px] font-medium text-white leading-tight truncate">
              {statusEmoji}
              {heightPx >= 28 ? ` ${formatTimeHHMM(start)}` : ''}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function VendorCalendarsPage(): React.ReactElement {
  const [anchor, setAnchor] = useState<Date>(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  })
  const [refreshToken, setRefreshToken] = useState(0)
  const [forceRefresh, setForceRefresh] = useState(false)

  const { start: weekStart, end: weekEnd } = weekRange(anchor)
  const startStr = localYMD(weekStart)
  const endStr   = localYMD(weekEnd)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['vendor-calendars', startStr, endStr, refreshToken],
    queryFn: () => fetchVendorCalendars(startStr, endStr, forceRefresh),
    staleTime: 14 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000,
  })

  useEffect(() => {
    if (forceRefresh) setForceRefresh(false)
  }, [refreshToken, forceRefresh])

  const handleManualRefresh = useCallback(() => {
    setForceRefresh(true)
    setRefreshToken((t) => t + 1)
  }, [])

  // Gatilho de evento de teste (não escreve no GHL) — dispara a confirmação C1
  // no WhatsApp para o número de teste (1º da allowlist). Respeita o modo teste.
  const [triggerMsg, setTriggerMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const testTrigger = useMutation({
    mutationFn: async () =>
      api<{ phone: string; scheduledAt: string; meetingId: string }>('/admin/ghl/test-trigger', {
        method: 'POST',
        body: JSON.stringify({ minutesFromNow: 180 }),
      }),
    onSuccess: (res) => {
      const when = new Date(res.scheduledAt).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
      setTriggerMsg({
        kind: 'ok',
        text: `Evento de teste criado para ${res.phone} (${when}). Confirmação C1 enfileirada — verifique o WhatsApp.`,
      })
      handleManualRefresh()
    },
    onError: (err: Error) => {
      const apiErr = err instanceof ApiError ? err : null
      const body = (apiErr?.bodyJson ?? null) as { error?: { message?: string } } | null
      setTriggerMsg({
        kind: 'err',
        text: body?.error?.message ?? apiErr?.message ?? 'Falha ao criar evento de teste.',
      })
    },
  })

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  const todayStr = localYMD(new Date())
  const hours    = Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => GRID_START_HOUR + i)
  const vendors  = data?.vendors ?? []
  const cachedAt = data?.cachedAt ? new Date(data.cachedAt) : null

  return (
    <div className="flex flex-col h-full gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold">Calendário dos Vendedores</h1>
        </div>
        <div className="flex items-center gap-2">
          {cachedAt !== null && (
            <span className="text-xs text-muted-foreground">
              Atualizado: {cachedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setTriggerMsg(null); testTrigger.mutate() }}
            disabled={testTrigger.isPending}
            title="Cria um agendamento de teste (não escreve no GHL) e dispara a confirmação no WhatsApp para o número de teste"
          >
            <FlaskConical className={cn('h-4 w-4 mr-1.5', testTrigger.isPending && 'animate-pulse')} />
            Evento de teste
          </Button>
          <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={isLoading} aria-label="Atualizar">
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => { const d = new Date(); d.setHours(0,0,0,0); setAnchor(d) }}>
            Hoje
          </Button>
          <div className="flex">
            <Button variant="outline" size="icon" className="rounded-r-none" onClick={() => setAnchor((d) => addDays(d, -7))} aria-label="Semana anterior">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="rounded-l-none border-l-0" onClick={() => setAnchor((d) => addDays(d, 7))} aria-label="Próxima semana">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <span className="text-sm font-medium min-w-[140px]">
            {weekStart.getDate()} {MONTH_LABELS[weekStart.getMonth()]} —{' '}
            {weekEnd.getDate()} {MONTH_LABELS[weekEnd.getMonth()]} {weekEnd.getFullYear()}
          </span>
        </div>
      </div>

      {/* Test-trigger feedback */}
      {triggerMsg !== null && (
        <div
          role="status"
          className={cn(
            'flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-sm',
            triggerMsg.kind === 'ok'
              ? 'border-green-300 bg-green-50 text-green-800'
              : 'border-red-300 bg-red-50 text-red-800',
          )}
        >
          <span>{triggerMsg.text}</span>
          <button
            type="button"
            onClick={() => setTriggerMsg(null)}
            className="opacity-60 hover:opacity-100"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>
      )}

      {/* Vendor legend */}
      {vendors.length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs">
          {vendors.map((v, idx) => {
            const color = vendorColor(idx)
            return (
              <span
                key={v.sellerId}
                className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-white font-medium"
                style={{ backgroundColor: color.border }}
              >
                {v.name.split(' ')[0]}
                {(v.fetchError !== undefined || v.freeSlotsError !== undefined) && (
                  <span className="opacity-70">(erro)</span>
                )}
              </span>
            )
          })}
          <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-muted-foreground">
            <span
              className="h-2 w-3 rounded-sm border border-dashed border-current opacity-60"
            />
            Disponível (cor do vendedor, translúcido)
          </span>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Erro ao carregar calendários: {error instanceof Error ? error.message : 'Erro desconhecido'}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && vendors.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-muted-foreground">
          <CalendarDays className="h-10 w-10 opacity-30" />
          <p className="text-sm">Nenhum vendedor com GHL configurado.</p>
        </div>
      )}

      {/* Calendar grid */}
      {vendors.length > 0 && (
        <div className="flex-1 overflow-auto rounded-md border">
          {/* Day header */}
          <div
            className="sticky top-0 z-10 grid border-b bg-background"
            style={{ gridTemplateColumns: `56px repeat(7, minmax(100px, 1fr))` }}
          >
            <div className="border-r" />
            {weekDays.map((day) => {
              const isToday = localYMD(day) === todayStr
              return (
                <div
                  key={localYMD(day)}
                  className={cn('border-r px-2 py-2 text-center text-xs', isToday && 'bg-primary/5')}
                >
                  <div className={cn('font-semibold', isToday && 'text-primary')}>
                    {WEEKDAY_LABELS[day.getDay()]} {day.getDate()}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Grid body */}
          <div
            className="grid"
            style={{ gridTemplateColumns: `56px repeat(7, minmax(100px, 1fr))` }}
          >
            {/* Time labels */}
            <div className="border-r">
              {hours.map((h) => (
                <div
                  key={h}
                  className="border-b text-right pr-2 text-xs text-muted-foreground leading-none"
                  style={{ height: PX_PER_HOUR }}
                >
                  <span className="block pt-1">{String(h).padStart(2, '0')}:00</span>
                </div>
              ))}
            </div>

            {/* Day columns */}
            {weekDays.map((day) => (
              <DayColumn
                key={localYMD(day)}
                day={day}
                vendors={vendors}
                isToday={localYMD(day) === todayStr}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
